import React, { useState } from 'react';
import {
	schemaSpec as schemaSpecPM,
	isNotEmpty as isNotEmptyPM,
} from '@techchange/pm-specs';
import PropTypes from 'prop-types';
import { Schema } from 'prosemirror-model';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	FormFeedback,
	FormGroup,
	Label,
	Popover,
} from 'reactstrap';

import { Button, IconButton } from '../../../../../_shared';
import { messages } from '../../../../../_shared/i18n/messages';

import { FIELD_TYPES, FORM_TYPES } from '../../../../constants';
import FormField from '../../../FormField';
import { PrivacyInput, SwitchInput } from '../../../FormInput';
import TextEditorInline from '../../../TextEditorInline';
import { useFormEditorContext } from '../../contexts/FormEditorContext';
import {
	FIELD_EDITOR_FIELDS,
	FORM_EDITOR_FIELDS,
} from '../../hooks/useFormEditorData';
import {
	formFieldDataShape,
	fieldInputValidationShape,
} from '../../utils/propTypes';

import FormEditorDummyInput from './components/FormEditorDummyInput';
import FormEditorFieldAnswersAndFeedbackModal
	from './components/FormEditorFieldAnswersAndFeedbackModal';
import FormEditorFieldOptions from './components/FormEditorFieldOptions';
import FormEditorFieldOptionsLikert from './components/FormEditorFieldOptionsLikert';

import './FormEditorField.scss';

const pmSchema = new Schema(schemaSpecPM);

const propTypes = {

	/**
	 * The form field.
	 */
	field: formFieldDataShape.isRequired,

	/**
	 * The index of the field with respect to the ordered
	 * list of fields in the form section.
	 */
	fieldIndex: PropTypes.number.isRequired,

	/**
	 * The input validation for the form field.
	 */
	fieldValidation: fieldInputValidationShape,

	/**
	 * Function to choose a drag source, injected by react-dnd.
	 */
	connectDragSource: PropTypes.func,

};

/**
 * Shared component to render a field in the form editor.
 *
 * @param {Object} props The props.
 * @returns {Object} The rendered component.
 */
const FormEditorField = (props) => {

	const {
		field,
		fieldIndex,
		fieldValidation,
		connectDragSource
	} = props;

	const intl = useIntl();

	const {
		formData,
		defaultLanguage,
		selectedLanguage,
		isEditingDefaultLanguage,
		orgName,
		showPrivacy,
		mobileView,
		filestack,
		actions,
		activeFieldID,
		setActiveFieldID,
	} = useFormEditorContext();

	const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
	const [isChoosingAnswersAndFeedback, setIsChoosingAnswersAndFeedback] = useState(false);

	const active = activeFieldID === field.id;

	const formType = formData[FORM_EDITOR_FIELDS.TYPE];
	const fieldType = field[FIELD_EDITOR_FIELDS.TYPE];

	// Get the form field content data for the selected language
	const activeTranslation =
		field[FIELD_EDITOR_FIELDS.CONTENTS].find(
			(content) => content.language === selectedLanguage
		);

	// Get the form field content data for the default language
	const defaultTranslation =
		field[FIELD_EDITOR_FIELDS.CONTENTS].find(
			(content) => content.language === defaultLanguage
		);

	// Get the validation related to the form field content data and selected language
	const validationState =
		fieldValidation?.[FIELD_EDITOR_FIELDS.CONTENTS]?.data?.[selectedLanguage];

	const showCorrectAnswersAndFeedback = formType === FORM_TYPES.QUIZ;

	const showAnswerChoiceOptions =
		fieldType === FIELD_TYPES.CHECKBOXES ||
		fieldType === FIELD_TYPES.RADIO ||
		fieldType === FIELD_TYPES.SELECT;

	// The settings button should only be visible for Radio and Select field types
	// for application forms with more than one section
	const showSettingsButton =
		(fieldType === FIELD_TYPES.RADIO || fieldType === FIELD_TYPES.SELECT) &&
		formType === FORM_TYPES.APPLICATION &&
		formData[FORM_EDITOR_FIELDS.SECTIONS].length > 1;

	// Only show navigation dropdowns for child form field options if field is
	// actively focused and has been set to be conditional
	const showNavOptions =
		showSettingsButton &&
		active &&
		field[FIELD_EDITOR_FIELDS.CONDITIONAL];

	const showLikertOptions = fieldType === FIELD_TYPES.LIKERT;

	const showFilterToggle = formType === FORM_TYPES.PROFILE && (
		fieldType === FIELD_TYPES.TEXT ||
		fieldType === FIELD_TYPES.CHECKBOXES ||
		fieldType === FIELD_TYPES.RADIO ||
		fieldType === FIELD_TYPES.SELECT
	);

	const showDummyInput =
		!showAnswerChoiceOptions &&
		!showLikertOptions;

	const settingsDropdownTargetID = `field-settings-dropdown-trigger-${field.id}`;
	const settingsDropdownContainerID = `field-settings-dropdown-container-${field.id}`;

	// Build the placeholder text for the field title value
	const placeholderTextForFieldTitle =
		isEditingDefaultLanguage
			? intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_LABEL_TEXT_FORM_FIELD_TITLE, {
				index: fieldIndex + 1,
			}) : defaultTranslation?.[FIELD_EDITOR_FIELDS.TITLE];

	// Build the placeholder text override for the field help value

	let placeholderTextOverrideForFieldHelp = null;

	if (!isEditingDefaultLanguage && isNotEmptyPM(defaultTranslation?.[FIELD_EDITOR_FIELDS.HELP])) {

		// Parse the ProseMirror JSON object to pull out the text content

		placeholderTextOverrideForFieldHelp =
			pmSchema.nodeFromJSON(defaultTranslation?.[FIELD_EDITOR_FIELDS.HELP])?.textContent;

	}

	const isTitleInvalid = validationState?.[FIELD_EDITOR_FIELDS.TITLE]?.invalid;

	return (
		<FormGroup
			className={`tc-form-editor-field py-3 ${active ? 'active-field' : ''}`}
			onClick={() => setActiveFieldID(field.id)}>
			<>
				<Label
					for={`${field.id}-title`}
					hidden>
					{placeholderTextForFieldTitle}
				</Label>
				<input
					className={`
						tc-material-input tc-material-input-label-or-legend
						tc-form-question-text
						${isTitleInvalid ? 'tc-material-input-invalid' : ''}
					`}
					aria-describedby={
						isTitleInvalid ? `${field.id}-title-feedback` : undefined
					}
					type="text"
					name={`${field.id}-title`}
					value={activeTranslation?.[FIELD_EDITOR_FIELDS.TITLE] || ''}
					placeholder={placeholderTextForFieldTitle}
					onChange={(event) => actions.fields.update(
						field.id,
						FIELD_EDITOR_FIELDS.TITLE,
						event.target.value,
					)} />
				{isTitleInvalid &&
					<FormFeedback
						id={`${field.id}-title-feedback`}
						aria-live="assertive">
						{validationState?.[FIELD_EDITOR_FIELDS.TITLE]?.error}
					</FormFeedback>
				}
			</>
			<TextEditorInline
				// Re-mount if language changes
				key={`tc-form-field-${field.id}-${selectedLanguage}-help-editor`}
				editorDomId={`tc-form-field-${field.id}-${selectedLanguage}-help-editor`}
				content={activeTranslation?.[FIELD_EDITOR_FIELDS.HELP]}
				placeholderText={
					placeholderTextOverrideForFieldHelp ||
					intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_PLACEHOLDER_TEXT_FORM_FIELD_HELP)
				}
				onChange={(newValue) => actions.fields.update(
					field.id,
					FIELD_EDITOR_FIELDS.HELP,
					newValue,
				)} />
			{showDummyInput &&
				<FormEditorDummyInput
					fieldId={field.id}
					type={fieldType}
					subtype={field[FIELD_EDITOR_FIELDS.SUBTYPE]}
					placeholder={activeTranslation?.[FIELD_EDITOR_FIELDS.PLACEHOLDER] || ''}
					onChangePlaceholder={(event) => actions.fields.update(
						field.id,
						FIELD_EDITOR_FIELDS.PLACEHOLDER,
						event.target.value,
					)}
					onChangeSubtype={(subtype) => actions.fields.update(
						field.id,
						FIELD_EDITOR_FIELDS.SUBTYPE,
						subtype,
					)} />
			}
			{showAnswerChoiceOptions &&
				<FormEditorFieldOptions
					// Re-mount if language changes
					key={`tc-form-field-${field.id}-${selectedLanguage}-options`}
					field={field}
					optionsValidation={fieldValidation?.[FIELD_EDITOR_FIELDS.OPTIONS]}
					showNavOptions={showNavOptions} />
			}
			{showLikertOptions &&
				<FormEditorFieldOptionsLikert
					fieldId={field.id}
					options={field[FIELD_EDITOR_FIELDS.OPTIONS] || []} />
			}
			<div className="tc-form-editor-field-actionsbar">
				{showCorrectAnswersAndFeedback &&
					<Button
						className={`
							tc-form-editor-field-action
							tc-form-editor-field-action-answers-and-feedback
						`}
						color="neutral"
						aria-label={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_ARIA_LABEL_TEXT_CHOOSE_ANSWERS_AND_FEEDBACK, {
							field_title: activeTranslation?.[FIELD_EDITOR_FIELDS.TITLE] || ''
						})}
						onClick={() => setIsChoosingAnswersAndFeedback((prev) => !prev)}>
						<i
							aria-hidden="true"
							className="fas fa-check-circle fa-lg me-1"></i>
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_EDITOR_FIELD_LABEL_AND_HOVER_TEXT_CHOOSE_ANSWERS_AND_FEEDBACK} />
					</Button>
				}
				{showPrivacy && isEditingDefaultLanguage &&
					<>
						<div className="tc-form-editor-field-action-privacy">
							<PrivacyInput
								orgName={orgName}
								value={field[FIELD_EDITOR_FIELDS.PRIVACY]}
								onChange={(newPrivacy) => actions.fields.update(
									field.id,
									FIELD_EDITOR_FIELDS.PRIVACY,
									newPrivacy,
								)} />
						</div>
						<div className="tc-form-editor-field-actionsbar-separator" />
					</>
				}
				{isEditingDefaultLanguage &&
					<div
						className="tc-form-editor-field-action-switch"
						title={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_HOVER_TEXT_REQUIRED_QUESTION)}>
						<SwitchInput
							name={`field-${field.id}-required`}
							value={field[FIELD_EDITOR_FIELDS.REQUIRED]}
							label={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_LABEL_TEXT_FORM_FIELD_REQUIRED)}
							isEditing
							isLabelInline
							onChange={(value) => actions.fields.update(
								field.id,
								FIELD_EDITOR_FIELDS.REQUIRED,
								value,
							)} />
					</div>
				}
				{isEditingDefaultLanguage &&
					<>
						<div className="tc-form-editor-field-actionsbar-separator" />
						{showFilterToggle &&
							<>
								<div
									className="tc-form-editor-field-action-switch"
									title={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_HOVER_TEXT_SHOW_AS_FILTER_QUESTION)}>
									<SwitchInput
										name={`field-${field.id}-filter`}
										value={field[FIELD_EDITOR_FIELDS.FILTER]}
										label={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_LABEL_TEXT_FORM_FIELD_SHOW_AS_FILTER)}
										isEditing
										isLabelInline
										onChange={(value) => actions.fields.update(
											field.id,
											FIELD_EDITOR_FIELDS.FILTER,
											value,
										)} />
								</div>
								<div className="tc-form-editor-field-actionsbar-separator" />
							</>
						}
						<div className="d-flex">
							<IconButton
								className="tc-form-editor-field-action"
								title={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_HOVER_TEXT_CLONE_QUESTION)}
								aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_EDITOR_ARIA_LABEL_CLONE_QUESTION, {
									field_title: activeTranslation?.[FIELD_EDITOR_FIELDS.TITLE] || ''
								})}
								icon="fas fa-copy fa-lg"
								size="30px"
								onClick={() => actions.fields.clone(field.id)} />
							<IconButton
								className="tc-form-editor-field-action"
								title={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_HOVER_TEXT_REMOVE_QUESTION)}
								aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_EDITOR_ARIA_LABEL_REMOVE_QUESTION, {
									field_title: activeTranslation?.[FIELD_EDITOR_FIELDS.TITLE] || ''
								})}
								icon="fas fa-trash fa-lg"
								size="30px"
								onClick={() => actions.fields.delete(field.id)} />
							{showSettingsButton &&
								<div
									id={settingsDropdownContainerID}
									className="form-field-settings-dropdown-container">
									<IconButton
										id={settingsDropdownTargetID}
										className="tc-form-editor-field-action"
										title={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_SETTINGS_DROPDOWN_BUTTON_TITLE)}
										aria-label={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_SETTINGS_DROPDOWN_BUTTON_ARIA_LABEL, {
											field_title: activeTranslation?.[FIELD_EDITOR_FIELDS.TITLE] || ''
										})}
										icon="fas fa-ellipsis-vertical fa-lg"
										size="30px"
										onClick={() => setIsSettingsDropdownOpen(true)} />
									<Popover
										id={`field-settings-dropdown-content-${field.id}`}
										container={settingsDropdownContainerID}
										target={settingsDropdownTargetID}
										placement="bottom-end"
										fade={false}
										isOpen={isSettingsDropdownOpen}
										toggle={() => setIsSettingsDropdownOpen((prev) => !prev)}
										trigger="legacy"
										hideArrow>
										<FormField
											id={`${field.id}-conditional-checkbox`}
											type={FIELD_TYPES.CHECKBOX}
											name={`${field.id}-conditional-checkbox`}
											label={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_FIELD_SETTINGS_CONDITIONAL_CHECKBOX_LABEL)}
											value={Boolean(field[FIELD_EDITOR_FIELDS.CONDITIONAL])}
											isEditing
											isLabelInline
											onChange={(name, value) => actions.fields.update(
												field.id,
												FIELD_EDITOR_FIELDS.CONDITIONAL,
												value,
											)} />
									</Popover>
								</div>
							}
						</div>
					</>
				}
			</div>
			{isEditingDefaultLanguage &&
				<div className="tc-form-editor-field-action-dnd">
					{connectDragSource && connectDragSource(
						<div
							className="tc-form-editor-field-action drag-source-handle"
							title={intl.formatMessage(messages.TC_COMPONENTS_FORM_EDITOR_HOVER_TEXT_MOVE_QUESTION)}>
							<i className="fas fa-grip-vertical" />
						</div>
					)}
				</div>
			}
			<div className="tc-form-editor-field-editing-indicator" />
			{showCorrectAnswersAndFeedback &&
				<FormEditorFieldAnswersAndFeedbackModal
					isOpen={isChoosingAnswersAndFeedback}
					field={field}
					mobileView={mobileView}
					filestack={filestack}
					onChange={actions.fields.update}
					onToggle={() => setIsChoosingAnswersAndFeedback((prev) => !prev)} />
			}
		</FormGroup>
	);

};

FormEditorField.propTypes = propTypes;

export default FormEditorField;
